import { useEffect, useRef, useState } from 'react';

import { ApiError } from '../lib/apiError';

/**
 * What a media type may be rendered as, if anything.
 *
 * **An allow-list, and the allow-list IS the security control** — see the hook below.
 */
export type PreviewKind = 'pdf' | 'image' | 'video' | 'none';

/**
 * `video` is OPT-IN. A caller that never asked for it is never handed it, so a viewer written
 * before it existed — which draws anything that is not an image in an `<iframe>` — cannot meet a
 * kind it does not branch on. Press releases opt in (12 Oct 2026).
 */
export interface PreviewOptions {
  video?: boolean;
}

/*
  The two renderable classes, keyed on the VERIFIED media type the server stored.

  SVG IS DELIBERATELY ABSENT. An `<img>` never executes script in an SVG — that is guaranteed
  by spec in every browser — so it would in fact be safe, and it is left out anyway: it is the
  one image type whose safety rests on a subtlety rather than on the format being inert, and
  nothing in this product can store one. A list that is obviously safe beats one that is
  safe-if-you-know-why.
*/
const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']);

const PDF_TYPE = 'application/pdf';

/*
  MP4 alone. A `<video>` hands the bytes to the media decoder, which executes nothing whatever they
  are — the same footing as an `<img>`. The test is whether the RENDERING is inert, and it is. Only
  the type the file store verifies by its `ftyp` box is listed; anything else is not rendered.
*/
const VIDEO_TYPES = new Set(['video/mp4']);

/** How a file of this media type may be shown, if at all. */
export function previewKindFor(contentType: string | null | undefined, options: PreviewOptions = {}): PreviewKind {
  if (!contentType) return 'none';

  const type = contentType.split(';')[0].trim().toLowerCase();

  if (type === PDF_TYPE) return 'pdf';
  if (IMAGE_TYPES.has(type)) return 'image';
  if (options.video && VIDEO_TYPES.has(type)) return 'video';

  return 'none';
}

/**
 * A stored file, as a URL the browser can render INERTLY — fetched on demand.
 *
 * **Every download endpoint refuses to serve anything inline, and this does not undo that.** The
 * endpoints set `Content-Disposition: attachment` and `nosniff` on purpose: an inline response
 * asks the browser to render, on this origin, content the server is merely holding — so anything
 * that slipped past the type checks becomes stored XSS. A preview built by pointing an `<iframe>`
 * at the endpoint would hand the control straight back, and a preview built from a blob URL
 * would do it too, because **a blob URL is same-origin with the page that made it**.
 *
 * Three things keep this safe, and all three are load-bearing:
 *
 * 1. **The type is the server's VERIFIED one** — confirmed against the magic bytes on write —
 *    never the extension on the uploaded name.
 * 2. **The allow-list is of INERT renderings**, not of trusted files. An image goes in an `<img>`,
 *    which executes nothing whatever the bytes are. A PDF goes to the browser's own sandboxed
 *    viewer. An MP4, for a caller that opted in, goes in a `<video>`. Anything else is not rendered
 *    at all.
 * 3. **The blob is re-typed on construction.** The object URL is built with an explicit `type`,
 *    so the iframe is handed `application/pdf` no matter what the response said — a payload that
 *    is secretly HTML reaches the PDF viewer, which declines it, rather than the HTML parser,
 *    which would run it.
 *
 * **ONE copy of that, for every preview in the product.** The Repository's document and its
 * archived versions, and Routing's attachments, each pass their own `load` — the fetch is theirs,
 * the re-typing and the object-URL lifecycle are this hook's. A second copy of the
 * security-bearing half is the drift this exists to prevent.
 *
 * **`key` names WHAT is being fetched, and it alone re-runs the fetch.** `load` is read through a
 * ref so a caller passing an inline arrow cannot loop the effect — the trap `useDataTable.fetchPage`
 * documents — and two strings cannot be got wrong the way a callback's identity can.
 *
 * **Nothing is fetched until the reader asks, and MOUNTING is how they ask.** The panel calling
 * this renders only while its tab is open; flipping away and back re-fetches, which is the right
 * trade for a file of tens of megabytes.
 */
export function useBlobPreview(
  key: string | undefined,
  contentType: string | null | undefined,
  load: (signal: AbortSignal) => Promise<{ blob: Blob }>,
  options: PreviewOptions = {},
): {
  kind: PreviewKind;
  url: string | null;
  loading: boolean;
  error: ApiError | null;
} {
  const kind = previewKindFor(contentType, options);

  /*
    The type the blob is pinned to — normalised off the same value `kind` was decided from, so
    the two cannot disagree. `previewKindFor` has already refused everything outside the
    allow-list, so by here this is one of seven known-inert types.
  */
  const verified = (contentType ?? '').split(';')[0].trim().toLowerCase();

  const loadRef = useRef(load);

  // Synced after render, never during it — a ref is never written in render.
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    Revoked when it is REPLACED, not when the fetch that replaces it starts. React runs this
    cleanup after the render carrying the new URL has committed, so nothing is ever pointing at
    a URL that has already been revoked — the pairing `usePersonPhoto` uses.
  */
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );

  useEffect(() => {
    if (!key || kind === 'none') {
      setUrl(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    loadRef
      .current(controller.signal)
      .then(({ blob }) => {
        if (controller.signal.aborted) return;

        /*
          RE-TYPED on construction, to the VERIFIED type this hook already decided to render —
          never to whatever the response happened to say. An empty `blob.type` would otherwise
          leave the browser to sniff, which is the one thing `nosniff` on the response exists
          to stop.
        */
        const typed = new Blob([blob], { type: verified });

        setUrl(URL.createObjectURL(typed));
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setUrl(null);
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load the preview.', 0));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    /*
      `verified` belongs here beside `kind`: two types can share one kind — `image/png` and
      `image/jpeg` are both `image` — so an effect keyed on the kind alone would keep a stale
      closure over the type it pins the blob to.
    */
  }, [key, kind, verified]);

  return { kind, url, loading, error };
}
